import { OfChatNotificationsService, TeamMemberDto } from '@api'
import { useUnit } from 'effector-react'
import { InfiniteData, useMutation, useQueryClient } from 'react-query'

import { useToast } from '@/components/ui/toast'
import { $creatorId } from '@/models/creators'
import {
  GetNotificationsResponse,
  Notification,
} from '@/modules/chatting/types'
import { handleError } from '@/utils/error'

export const useReassignNotification = ({
  notification,
  previousNotification,
}: {
  notification: Notification
  previousNotification?: Notification
}) => {
  const toast = useToast()
  const creatorId = useUnit($creatorId)
  const queryClient = useQueryClient()

  const onSuccess = (_: unknown, userId: TeamMemberDto['userId']) => {
    toast.success('Successfully reassigned the sale!')
    queryClient.setQueriesData<
      InfiniteData<GetNotificationsResponse> | undefined
    >('notifications', (prev) => {
      if (!prev) {
        return prev
      }

      const newNotification = {
        ...notification,
        assignedUserId: userId,
      }

      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          list: page.list.map((notification: Notification) =>
            notification.id === newNotification.id
              ? newNotification
              : notification,
          ),
        })),
      }
    })
  }

  const reassignTip = useMutation(
    (userId: TeamMemberDto['userId']) =>
      OfChatNotificationsService.assignTip({
        creatorId: creatorId!,
        requestBody: {
          userId,
          internalId: notification.id,
          previousInternalId: previousNotification?.id,
        },
      }),
    {
      onSuccess,
      onError: (error) => {
        handleError(error, 'Something went wrong while reassigning tip')
      },
    },
  )

  const reassignMediaMessage = useMutation(
    (userId: TeamMemberDto['userId']) =>
      OfChatNotificationsService.assignMediaMessage({
        creatorId: creatorId!,
        requestBody: {
          userId,
          onlyFansMessageId: notification.referencingMessageId,
          purchaseDate: notification.createdAt,
          fanId: notification.user!.id,
        },
      }),
    {
      onSuccess,
      onError: (error) => {
        handleError(
          error,
          'Something went wrong while reassigning media message',
        )
      },
    },
  )

  const handleReassign = (userIdString: string) => {
    if (!creatorId) {
      throw new Error('Creator not selected')
    }
    const userId = Number(userIdString)

    if (notification.type === 'tip') {
      return reassignTip.mutateAsync(userId)
    }

    return reassignMediaMessage.mutateAsync(userId)
  }

  return {
    onReassign: handleReassign,
    isLoading: reassignTip.isLoading || reassignMediaMessage.isLoading,
  }
}
